import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Capacitor } from '@capacitor/core';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { signIn, signInWithGoogle, signInWithApple } from '@/api/auth';
import { ApiError } from '@/api/client';
import { emitQrEvent } from '@/api/qrAnalytics';
import { getOrCreateQrSessionId } from '@/lib/qrSession';
import {
  isNativeApp,
  nativeGoogleSignIn,
  nativeGoogleAvailable,
  isUserCancellation
} from '@/lib/nativeGoogle';
import { nativeAppleSignIn } from '@/lib/nativeApple';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Display } from '@/components/ui/Typography';
import { loginSchema, type LoginFormData } from '@/lib/validation';
import { useAppConfig } from '@/lib/config';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';

interface LocationState {
  from?: { pathname: string };
}

// Login em 2 passos (design Saulo, nss-auth.jsx v2 — modelo direto ref. Tupi):
// 'launcher' = logo + "Entre ou cadastre-se" + pilha Apple → Google → ou →
// Entrar → Criar conta. 'email' = passo do e-mail (campos + Continuar).
type Step = 'launcher' | 'email';

export default function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
  const [step, setStep] = useState<Step>('launcher');
  const [apiError, setApiError] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const { kbOpen, vvHeight } = useKeyboardViewport();

  // Lê em runtime do /auth/config (carregado no boot via main.tsx). Antes era
  // build-time via VITE_GOOGLE_CLIENT_ID — mover pra runtime evita rebuild
  // do PWA quando o backend troca de Client ID.
  const googleClientId = useAppConfig().google_client_id;

  // No app nativo (Capacitor) o widget web do Google não funciona — o Google
  // bloqueia OAuth em webview. Usamos o plugin nativo. No PWA, segue o widget.
  const native = isNativeApp();
  // No iOS, o botão nativo só aparece se o `iOSClientId` estiver configurado no
  // build (VITE_GOOGLE_IOS_CLIENT_ID) — sem ele o login nativo do Google não
  // inicializa, então escondemos em vez de mostrar um botão que não responde.
  const showNativeGoogle = native && nativeGoogleAvailable();
  // Sign in with Apple só no iOS nativo (Guideline 4.8). No Android/PWA não.
  // O design prevê o botão sempre; esta condição de runtime decide onde ele
  // realmente renderiza — mesmo bundle nos 3 canais.
  const isIOS = native && Capacitor.getPlatform() === 'ios';

  async function onSubmit(data: LoginFormData) {
    setApiError(null);
    try {
      const user = await signIn(data.email, data.password);
      maybeEmitQrAuthCompleted(from);
      if (!user.profile_complete) {
        navigate('/complete-profile', { replace: true, state: { from: { pathname: from } } });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.status === 401 ? 'Email ou senha inválidos' : err.message);
      } else {
        setApiError('Não foi possível conectar ao servidor');
      }
    }
  }

  // Troca o idToken do Google (web ou nativo) por sessão Bow e navega.
  async function completeGoogleAuth(idToken: string) {
    setApiError(null);
    setGoogleBusy(true);
    try {
      const user = await signInWithGoogle(idToken);
      maybeEmitQrAuthCompleted(from);
      if (!user.profile_complete) {
        navigate('/complete-profile', { replace: true, state: { from: { pathname: from } } });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(
          err.status === 401
            ? 'Sessão Google inválida. Tente novamente.'
            : err.message
        );
      } else {
        setApiError('Não foi possível conectar ao servidor');
      }
    } finally {
      setGoogleBusy(false);
    }
  }

  // Web (PWA): widget @react-oauth/google devolve o credential (idToken).
  async function onGoogleSuccess(resp: CredentialResponse) {
    if (!resp.credential) {
      setApiError('Google não retornou um token válido');
      return;
    }
    await completeGoogleAuth(resp.credential);
  }

  // Nativo (Capacitor): plugin de Sign-In nativo devolve o idToken.
  async function onNativeGoogle() {
    if (!googleClientId || googleBusy) return;
    let idToken: string;
    try {
      idToken = await nativeGoogleSignIn(googleClientId);
    } catch (err) {
      // Cancelamento do usuário é silencioso (sem erro vermelho pra uma ação
      // intencional). Qualquer outra falha (init/config/rede) mostra feedback —
      // senão uma regressão vira de novo "clica e nada acontece".
      if (!isUserCancellation(err)) {
        setApiError('Não foi possível entrar com o Google. Tente novamente.');
      }
      return;
    }
    await completeGoogleAuth(idToken);
  }

  // iOS nativo: Sign in with Apple devolve o identityToken (+ nome no 1º login).
  async function onNativeApple() {
    if (appleBusy) return;
    let idToken: string;
    let name: string | null;
    try {
      ({ idToken, name } = await nativeAppleSignIn());
    } catch {
      return; // cancelamento/erro — silencioso
    }
    setApiError(null);
    setAppleBusy(true);
    try {
      const user = await signInWithApple(idToken, name);
      maybeEmitQrAuthCompleted(from);
      navigate(user.profile_complete ? from : '/complete-profile', {
        state: user.profile_complete ? undefined : { from: { pathname: from } },
        replace: true
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(
          err.status === 401
            ? 'Sessão Apple inválida. Tente novamente.'
            : err.message
        );
      } else {
        setApiError('Não foi possível conectar ao servidor');
      }
    } finally {
      setAppleBusy(false);
    }
  }

  // ── Passo do e-mail — NSS §5.3: com teclado aberto o frame encolhe pra
  // viewport visual e campos + Continuar ficam acima do teclado. ─────────
  if (step === 'email') {
    return (
      <div
        className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
        style={{
          height: kbOpen && vvHeight !== null ? `${vvHeight}px` : '100dvh',
          paddingTop: kbOpen ? 12 : 'max(2rem, env(safe-area-inset-top))',
          paddingBottom: kbOpen ? 12 : 'max(1.75rem, env(safe-area-inset-bottom))'
        }}
      >
        <div className="flex flex-shrink-0 items-center gap-2.5">
          <button
            type="button"
            onClick={() => setStep('launcher')}
            aria-label="Voltar"
            className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
          >
            <ChevronLeftIcon />
          </button>
          <Display size={22}>Entrar com e-mail</Display>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* Campos logo abaixo do header — com o input já visível no focus o
              iOS não pana a página atrás dele (bug reportado 2026-07-04: a
              tela subia inteira ao tocar no e-mail). O espaçador flexível fica
              ENTRE os campos e o CTA: no repouso o botão desce pro alcance do
              polegar; com teclado aberto ele cola acima do teclado (§5.3). */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col gap-3 pt-6"
          >
            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              placeholder="ana@email.com"
              error={errors.email?.message}
              {...register('email')}
            />
            <div className="flex flex-col gap-1">
              <Input
                label="Senha"
                type="password"
                autoComplete="current-password"
                error={errors.password?.message}
                {...register('password')}
              />
              <div className="flex justify-end px-1">
                <Link
                  to="/forgot-password"
                  className="text-[13px] text-bow-royal/55 hover:text-bow-royal"
                >
                  Esqueci minha senha
                </Link>
              </div>
            </div>
            {apiError && <p className="px-1 text-sm text-bow-red">{apiError}</p>}
            <div className="min-h-2 flex-1" />
            {/* onPointerDown preventDefault: tap no CTA com teclado aberto não
                pode blur-ar o input antes do click (layout mudaria e erraria). */}
            <Button
              type="submit"
              disabled={isSubmitting}
              onPointerDown={(e) => e.preventDefault()}
            >
              {isSubmitting ? 'Entrando…' : 'Continuar'}
              <ArrowRightIcon />
            </Button>
          </form>

          <p
            className={`mt-4 flex-shrink-0 text-center text-[13px] text-bow-royal/55 ${
              kbOpen ? 'hidden' : ''
            }`}
          >
            Novo por aqui?{' '}
            <Link
              to="/register"
              state={location.state}
              className="font-semibold text-bow-royal"
            >
              Crie sua conta
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Launcher (design Saulo v2, ref. Tupi): sem hero de marketing e sem
  // decisão login-vs-cadastro no topo. Logo + heading centrados; pilha de
  // ações ancorada — Apple (só iOS nativo) → Google → ou → Entrar → Criar
  // conta. Naturalmente baixo: cabe com folga nos dois alvos. ─────────────
  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
      style={{
        height: '100dvh',
        paddingTop: 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1.75rem, env(safe-area-inset-bottom))'
      }}
    >
      {/* topo: marca + heading, centralizados na folga */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto text-center [@media(max-height:740px)]:gap-3.5">
        <img
          src="/logo/logo-bow-01.png"
          alt="Bow"
          className="h-10 w-auto [@media(max-height:740px)]:h-[34px]"
        />
        <div>
          <Display size={32} className="leading-[1.08] [@media(max-height:740px)]:!text-[27px]">
            Entre ou
            <br />
            cadastre-se
          </Display>
          <p className="mx-auto mt-3 max-w-[250px] text-sm leading-snug text-bow-royal/55 [@media(max-height:740px)]:mt-2">
            É rápido. Em segundos você já está recarregando.
          </p>
        </div>
      </div>

      {/* base: Apple → Google → ou → Entrar → Criar conta */}
      <div className="flex flex-shrink-0 flex-col gap-2.5">
        {isIOS && (
          <button
            type="button"
            onClick={onNativeApple}
            disabled={appleBusy}
            className="inline-flex h-[54px] items-center justify-center gap-2.5 rounded-full bg-[#111111] text-[15.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            <AppleGlyph />
            Continuar com Apple
          </button>
        )}

        {googleClientId &&
          (showNativeGoogle ? (
            <button
              type="button"
              onClick={onNativeGoogle}
              disabled={googleBusy}
              className="inline-flex h-[54px] items-center justify-center gap-2.5 rounded-full border-[1.5px] border-bow-royal/20 bg-white text-[15.5px] font-semibold text-bow-royal transition hover:bg-bow-royal/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <GoogleGlyph />
              Continuar com Google
            </button>
          ) : !native ? (
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={onGoogleSuccess}
                onError={() => setApiError('Falha no login com Google')}
                useOneTap={false}
                width="320"
                text="continue_with"
                shape="pill"
              />
            </div>
          ) : null)}

        {(googleBusy || appleBusy) && (
          <p className="text-center text-xs text-bow-royal/60">
            {appleBusy ? 'Autenticando com Apple…' : 'Autenticando com Google…'}
          </p>
        )}
        {apiError && (
          <p className="text-center text-sm text-bow-red">{apiError}</p>
        )}

        {(isIOS || googleClientId) && (
          <div className="my-0.5 flex items-center gap-3 text-xs font-medium text-bow-royal/40">
            <span className="h-px flex-1 bg-bow-royal/10" />
            ou
            <span className="h-px flex-1 bg-bow-royal/10" />
          </div>
        )}

        <button
          type="button"
          onClick={() => setStep('email')}
          className="inline-flex h-[54px] items-center justify-center rounded-full border-[1.5px] border-bow-royal/[0.22] bg-transparent text-[15.5px] font-semibold text-bow-royal transition hover:bg-bow-royal/5"
        >
          Já tenho conta · Entrar
        </button>
        <Link
          to="/register"
          state={location.state}
          className="inline-flex h-[54px] items-center justify-center gap-2 rounded-full bg-bow-royal text-[15.5px] font-semibold text-bow-cream transition hover:bg-bow-royal/90"
        >
          Criar conta
          <ArrowRightIcon />
        </Link>
        {/* Recuperação de senha acessível já na tela inicial — antes só
            aparecia dentro do passo de e-mail (feedback Saulo 2026-07-12). */}
        <Link
          to="/forgot-password"
          className="mt-0.5 text-center text-[13px] text-bow-royal/55 transition hover:text-bow-royal"
        >
          Esqueci minha senha
        </Link>
      </div>
    </div>
  );
}

/**
 * Emite `auth_completed` no qr_analytics quando o login completa em
 * resposta a um deeplink do QR físico (`from = /qr/{stationId}/{connector}`
 * ou `/qr/{stationId}`). Caminhos de login normais não emitem.
 */
function maybeEmitQrAuthCompleted(from: string): void {
  const m = /^\/qr\/([^/?#]+)/.exec(from);
  if (!m) return;
  emitQrEvent({
    qrSessionId: getOrCreateQrSessionId(),
    hubStationId: decodeURIComponent(m[1]),
    eventType: 'auth_completed'
  });
}

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden>
      <path d="M16.36 12.78c.02 2.36 2.08 3.15 2.1 3.16-.02.06-.33 1.13-1.09 2.23-.65.96-1.33 1.9-2.4 1.92-1.05.02-1.39-.62-2.59-.62-1.2 0-1.58.6-2.57.64-1.03.04-1.82-1.03-2.48-1.99-1.35-1.96-2.38-5.53-1-8.95.69-1.69 1.92-2.76 3.32-2.78 1.02-.02 1.98.68 2.6.68.62 0 1.79-.85 3.02-.72.51.02 1.96.21 2.89 1.56-.08.05-1.72 1.01-1.7 3zM14.6 5.42c.55-.67.92-1.6.82-2.52-.79.03-1.75.53-2.32 1.19-.51.59-.96 1.53-.84 2.44.88.07 1.78-.45 2.34-1.11z" />
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 18 18" width="19" height="19" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.06l3.01-2.34Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
